<template>
    <div v-if="addOnIsDisabled">
        <disabled-message />
    </div>
    <div v-else>
        <b-row
            class="py-3"
            :class="{ 'sticky-row-header': !_isTvView && _isEnhancedLayout }"
        >
            <b-col md="7">
                <h1 class="d-sm-inline">
                    {{ titleDateLabel }}
                </h1>
                <a href="#" @click.prevent>{{ timezoneLabel }}</a>
            </b-col>
            <b-col class="text-right">
                <b-button-group class="mx-1 button-arrow">
                    <b-button
                        @click="changeDate('prev')"
                        v-b-tooltip.hover.top
                        title="Previous day"
                    >
                        <i class="fas fa-chevron-left px-1"></i>
                    </b-button>
                    <div class="input-group date-group">
                        <div class="input-group-append">
                            <button
                                class="btn btn-secondary datepickerbutton"
                                type="button"
                                v-b-tooltip.hover.top
                                title="Change date"
                            >
                                <i class="far fa-calendar"></i>
                            </button>
                        </div>
                        <date-picker
                            id="datepicker-dashboard-boarding"
                            wrap
                            name="date"
                            :config="datePickerOptions"
                            v-model="date"
                            @dp-change="changeSpecificDate"
                        />
                    </div>
                    <b-button
                        @click="changeDate('next')"
                        v-b-tooltip.hover.top
                        title="Next day"
                    >
                        <i class="fas fa-chevron-right px-1"></i>
                    </b-button>
                </b-button-group>
            </b-col>
        </b-row>

        <b-container fluid>
            <b-row>
                <b-col sm="12" md="12" lg="4" class="mb-sm-3">
                    <div class="panel">
                        <dashboard-panel-heading
                            :title="`Checking In (${listCheckingIn.length})`"
                            :isLoadingOptions="isLoadingOptionCheckIn"
                            :options="[
                                {
                                    icon: 'famfamfam-silk printer',
                                    label: 'Print Reservation List',
                                    onclick: () => exportReservation('pdf')
                                },
                                {
                                    icon: 'famfamfam-silk email',
                                    label: 'Email Reservation List',
                                    onclick: () => exportReservation('email')
                                },
                                {
                                    icon: 'famfamfam-silk printer',
                                    label: 'Print Boarding Forms',
                                    onclick: printBoardingForm
                                }
                            ]"
                        />

                        <div v-if="isLoading">
                            <div class="skeleton"></div>
                            <div class="skeleton"></div>
                        </div>
                        <draggable
                            v-else
                            :class="{
                                listgroup: true,
                                empty: listCheckingIn.length <= 0
                            }"
                            :list="listCheckingIn"
                            group="records"
                            :disabled="true"
                        >
                            <dashboard-card
                                @onclick="handleClickCard(record, 'CHECKIN')"
                                disabled
                                v-for="record in listCheckingIn"
                                :key="record.id"
                                type="checking-in"
                                :room="record.room"
                                :period="record.period"
                                :patientName="record.patientName"
                                :patientLink="record.patientLink"
                                :species="record.species"
                            />
                        </draggable>
                    </div>
                </b-col>
                <b-col sm="12" md="12" lg="4" class="mb-sm-3">
                    <div class="panel">
                        <dashboard-panel-heading
                            :title="`Checking Out (${listCheckingOut.length})`"
                            :isLoadingOptions="isLoadingOptionCheckOut"
                            :options="[
                                {
                                    icon: 'famfamfam-silk printer',
                                    label: 'Print Check Out List',
                                    onclick: () => exportCheckout('pdf')
                                },
                                {
                                    icon: 'famfamfam-silk email',
                                    label: 'Email Check Out List',
                                    onclick: () => exportCheckout('email')
                                }
                            ]"
                        />

                        <div v-if="isLoading">
                            <div class="skeleton"></div>
                            <div class="skeleton"></div>
                        </div>
                        <draggable
                            v-else
                            :class="{
                                listgroup: true,
                                empty: listCheckingOut.length <= 0
                            }"
                            :list="listCheckingOut"
                            group="records"
                            :disabled="true"
                        >
                            <dashboard-card
                                @onclick="handleClickCard(record, 'CHECKOUT')"
                                v-for="record in listCheckingOut"
                                :key="record.id"
                                :type="getCheckoutType(record)"
                                :room="record.room"
                                :period="record.period"
                                :patientName="record.patientName"
                                :patientLink="record.patientLink"
                                :species="record.species"
                            />
                        </draggable>
                    </div>
                </b-col>
                <b-col sm="12" md="12" lg="4" class="mb-sm-3">
                    <div class="panel">
                        <dashboard-panel-heading
                            :title="`Currently Boarded (${listCurrentlyBoarded.length})`"
                        />

                        <div v-if="isLoading">
                            <div class="skeleton"></div>
                            <div class="skeleton"></div>
                        </div>
                        <draggable
                            v-else
                            :class="{
                                listgroup: true,
                                empty: listCurrentlyBoarded.length <= 0
                            }"
                            :list="listCurrentlyBoarded"
                            group="records"
                            :disabled="true"
                        >
                            <dashboard-card
                                @onclick="handleClickCard(record, 'CHECKOUT')"
                                v-for="record in listCurrentlyBoarded"
                                :key="record.id"
                                type="checked-in"
                                :room="record.room"
                                :period="record.period"
                                :patientName="record.patientName"
                                :patientLink="record.patientLink"
                                :species="record.species"
                            />
                        </draggable>
                    </div>
                </b-col>
            </b-row>
        </b-container>

        <dashboard-reservation-modal
            ref="modalReservation"
            @refresh="fetchList"
        />

        <boarding-form ref="boardingFormWindow" />
        <print-email-reservation-list ref="boardingReservationWindow" />
    </div>
</template>

<script>
import draggable from "vuedraggable";
import DashboardCard from "./dashboard-card.vue";
import DashboardPanelHeading from "./dashboard-panel-heading.vue";
import moment from "moment-timezone";
import DashboardReservationModal from "./dashboard-reservation-modal.vue";
import BoardingForm from "@/module/schedule/boarding/print-form.vue";
import PrintEmailReservationList from "@/module/schedule/boarding/reservation-list.vue";
import BoardingService from "@/services/boarding.js";
import DisabledMessage from "./disabled-message.vue";

export default {
    components: {
        DashboardPanelHeading,
        DashboardCard,
        draggable,
        DashboardReservationModal,
        // DashboardCheckinModal,
        BoardingForm,
        PrintEmailReservationList,
        DisabledMessage
    },
    data() {
        return {
            addOnIsDisabled: false,
            isLoading: true,
            date: new Date(),
            datePickerOptions: {
                format: ""
            },
            listCheckingIn: [],
            listCheckingOut: [],
            listCurrentlyBoarded: [],
            isLoadingOptionCheckIn: false,
            isLoadingOptionCheckOut: false,
            timezoneLabel: ""
        };
    },
    computed: {
        titleDateLabel() {
            if (this.date) {
                let m = this.currentDateMoment();
                return m.format("dddd, MMMM DD, YYYY");
            }
            return "-";
        }
    },
    async mounted() {
        let isEnabled = await BoardingService.checkIsAddOnEnabled();
        if (!isEnabled) {
            this.addOnIsDisabled = true;
            return;
        }

        await this._getSetting();

        this.datePickerOptions = {
            format: this.getDateFormat(),
            timeZone: this._timeZone
        };

        this.fetchList(moment().tz(this._timeZone));
        this.timezoneLabel = this._timeZone.toString().replaceAll("_", "");
    },
    methods: {
        getCheckoutType(record) {
            if (
                record._raw &&
                record._raw.status &&
                record._raw.status.id === 2
            ) {
                return "checked-out";
            } else {
                return "checking-out";
            }
        },
        currentDateMoment() {
            return moment.tz(this.date, this.getDateFormat(), this._timeZone);
        },
        printBoardingForm() {
            let momentDate = this.currentDateMoment();
            this.$refs["boardingFormWindow"].open(momentDate);
        },
        async exportReservation(type = "pdf") {
            let momentDate = this.currentDateMoment();

            try {
                if (type === "pdf") {
                    this.isLoadingOptionCheckIn = true;

                    let dateValue = momentDate.valueOf();
                    dateValue = dateValue.toString();
                    dateValue = dateValue.substr(0, 10);
                    dateValue = Number(dateValue);

                    let res = await this.$http.post(
                        `${process.env.BARRAMUNDI}schedule/boarding/export-reservation`,
                        {
                            type: "reservation_form",
                            start: dateValue,
                            end: dateValue,
                            export_type: 0, // pdf
                            one_day_view: true
                        }
                    );

                    if (
                        res.data.response.message.state === "1" ||
                        res.data.response.message.state === 1
                    ) {
                        throw new Error(res.data.response.message.message);
                    } else {
                        window.open(res.data.response.message.path, "_blank");
                    }

                    this.isLoadingOptionCheckIn = false;
                } else {
                    this.$refs["boardingReservationWindow"].open(
                        type === "email" ? true : false,
                        momentDate.startOf("day"),
                        momentDate.endOf("day"),
                        false,
                        true // fixed date
                    );
                }
            } catch (error) {
                this._message(error.message, "danger");
                this.isLoadingOptionCheckIn = false;
            }
        },
        async exportCheckout(type = "pdf") {
            try {
                let momentDate = this.currentDateMoment();

                if (type === "pdf") {
                    this.isLoadingOptionCheckOut = true;

                    let res = await this.$http.get(
                        `${process.env.BARRAMUNDI}schedule/boarding-dashboard/export-checkout-list`,
                        {
                            params: {
                                date: momentDate.format(this.getDateFormat()),
                                action: ""
                            }
                        }
                    );

                    if (res.data.response.resources === false) {
                        throw new Error("No records available.");
                    } else if (res.data.response.resources.state === 1) {
                        throw new Error(res.data.response.resources.message);
                    } else {
                        window.open(res.data.response.resources.path, "_blank"); // open pdf
                    }

                    this.isLoadingOptionCheckOut = false;
                } else if (type === "email") {
                    this.$refs["boardingReservationWindow"].open(
                        type === "email" ? true : false,
                        momentDate.startOf("day"),
                        momentDate.endOf("day"),
                        true, // checkout
                        true // fixed date
                    );
                }
            } catch (error) {
                this._message(error.message, "danger");
                this.isLoadingOptionCheckOut = false;
            }
        },
        async fetchList() {
            try {
                this.isLoading = true;
                let m = this.currentDateMoment();

                let res = await this.$http.get(
                    `${process.env.BARRAMUNDI}schedule/boarding-dashboard`,
                    {
                        params: {
                            date: m.format(this.getDateFormat())
                        }
                    }
                );

                if (
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code === 200
                ) {
                    let list = res.data.response.resources;

                    this.listCheckingIn = this.convertToCardList(
                        list.filter(r => r.column === "checkin")
                    );

                    let listCheckingOut = this.convertToCardList(
                        list.filter(r => r.column === "checkout")
                    );
                    listCheckingOut.sort(
                        (a, b) => a._raw.status.id - b._raw.status.id
                    );
                    this.listCheckingOut = listCheckingOut;

                    this.listCurrentlyBoarded = this.convertToCardList(
                        list.filter(r => r.column === "current")
                    );

                    this.isLoading = false;
                } else {
                    throw new Error("Unable to fetch records.");
                }
            } catch (e) {
                this.isLoading = false;
                this._message("Unable to fetch records", "danger");
            }
        },
        getPeriodLabel(start, end, isIncludeCheckoutDate = true) {
            let momentStart = moment(start);
            let momentEnd = moment(end);
            let nights = momentEnd
                .endOf("day")
                .diff(momentStart.startOf("day"), "days");
            let periodLabel = "--";

            if (nights > 1) {
                periodLabel = `${nights} nights`;
            } else {
                periodLabel = `${nights} night`;
            }

            if (isIncludeCheckoutDate) {
                periodLabel += ` (${momentEnd.format(this.getDateFormat())})`; // checkout date
            }

            return periodLabel;
        },
        convertToCardList(list = []) {
            let result = [];

            for (let item of list) {
                let patientName = "--";
                let patientLink = "";
                let roomName = "--";
                let speciesName = "--";

                if (item.patient) {
                    patientName = item.patient.name;
                    patientLink = "/view/patient/profile/" + item.patient.encoded_id; // prettier-ignore

                    let breeds = item.patient.patient_breeds;

                    if (breeds && breeds[0]) {
                        speciesName = breeds[0].species_name;
                    }
                }

                if (item.room) {
                    roomName = item.room.name;
                }

                let periodLabel = this.getPeriodLabel(item.start, item.end);

                let card = {
                    id: item.id,
                    room: roomName,
                    period: periodLabel,
                    patientName: patientName,
                    patientLink: patientLink,
                    species: speciesName,
                    _raw: item
                };

                result.push(card);
            }

            return result;
        },
        handleClickCard(record, type) {
            let {
                client,
                patient,
                start,
                end,
                room,
                staff,
                default_provider,
                letter,
                soap,
                invoice,
                note,
                status
            } = record._raw;
            let species = [];
            let dateFormat = this.getDateFormat();
            let roomFee = 0;

            let statusNumber = status?.id ?? 0;
            if (statusNumber) {
                statusNumber = Number(statusNumber);
            }

            for (let breed of patient.patient_breeds) {
                species.push(breed.species_name);
            }

            if (room.room_fees && room.room_fees[0]) {
                roomFee = room.room_fees[0].price;
            }

            let documents = [];
            if (letter) {
                documents.push({
                    id: letter.id,
                    label: letter.title,
                    ci_url: letter.ci_url,
                    encoded_id: letter.encoded_id,
                    type: "letter"
                });
            }
            if (soap) {
                documents.push({
                    id: soap.id,
                    label: soap.label,
                    ci_url: soap.ci_url,
                    encoded_id: null,
                    type: "soap"
                });
            }

            let clientMobile = null;
            if (client.mobile && client.mobile !== "") {
                clientMobile = `+${client.mobile_country_code}${client.mobile}`;
            }

            let defaultProvider = null;
            if (default_provider) {
                defaultProvider = default_provider.business.label + " Provider";
            }

            let cardRecord = {
                recordId: record.id,
                clientName: client.label,
                clientMobile: clientMobile,
                clientEncodedId: client.encoded_id,
                patientName: patient.name,
                patientSpecies: species.join(", "),
                patientEncodedId: patient.encoded_id,
                checkingIn: moment(start).tz(this._timeZone).format(dateFormat),
                checkingOut: moment(end).tz(this._timeZone).format(dateFormat),
                nights: this.getPeriodLabel(start, end, false),
                roomName: room.name,
                roomFee: roomFee,
                provider: staff ? staff.label : defaultProvider,
                documents: documents,
                invoice: invoice,
                notes: note,
                status: statusNumber
            };

            this.$refs.modalReservation.open({
                record: cardRecord,
                type: type,
                isNewCalendarOn: true // 'true' = vue version. 'false' = CI version
            });
        },
        moveCard({ recordId, from, to }) {
            const CHECKIN = "CHECKIN";
            const BOARDING = "BOARDING";

            let cardObject = null;

            if (from === CHECKIN && to === BOARDING) {
                cardObject = this.listCheckingIn.find(
                    card => card.id === recordId
                );

                if (cardObject) {
                    this.listCheckingIn = this.listCheckingIn.filter(
                        card => card.id !== recordId
                    );

                    this.listCurrentlyBoarded.push(cardObject);
                }
            }
        },
        changeSpecificDate(value) {
            this.date = value.date.toDate();
            this.fetchList();
        },
        changeDate(type) {
            let momentDate = this.currentDateMoment();
            let res;

            if (type === "next") {
                res = momentDate.tz(this._timeZone).add(1, "day");
            } else if (type === "prev") {
                res = momentDate.tz(this._timeZone).subtract(1, "day");
            } else {
                return;
            }

            this.date = res.toDate();
            this.fetchList();
        }
    }
};
</script>

<style scoped>
.panel {
    background-color: #fff;
    border: 1px solid #ececec;
    padding: 5px;
    padding-top: 15px;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
}
.listgroup {
    flex: 1;
    min-height: 150px;
    display: flex;
    flex-direction: column;
}
.listgroup.empty::after {
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    content: "No item available.";
    color: #b4b4b4;
    font-size: 13px;
}
.skeleton {
    margin: 5px 7px;
    margin-bottom: 15px;
    height: 40px;
    border-radius: 3px;
    animation-duration: 7s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: loading;
    animation-timing-function: linear;
    background: #eeeeee;
    background: linear-gradient(to right, #f0f0f0 8%, #e4e4e4 30%, #f0f0f0 33%);
}
input#datepicker-dashboard-boarding {
    visibility: hidden;
    position: absolute;
    top: 0;
}
@keyframes loading {
    0% {
        background-position: -800px 0;
    }
    100% {
        background-position: 800px 0;
    }
}
</style>
